<template>
  <section class="title-animation-section full--height" :style="sectionStyle">
    <v-container fill-height class="pa-0 text-xs-center">
      <v-layout align-center justify-center>
        <v-flex>
          <span v-if="!gifFallback" class="title-animation" :style="titleStyle">A</span>
        </v-flex>
      </v-layout>
    </v-container>
  </section>
</template>

<script>
import '../../node_modules/@hughsk/fulltilt/dist/fulltilt'
import GyroNorm from 'gyronorm'

export default {
  name: 'title-animation',
  data () {
    return {
      maxWght: 200,
      minWght: 50,
      minBlur: 0,
      minWindow: 0,
      maxWindow: 0,
      tickingMouse: false,
      tickingScroll: false,
      gifFallback: false,
      gyroTicking: false,
      gn: new GyroNorm(),
      titleStyle: {
        'font-variation-settings': '"wght" 200'
      },
      sectionStyle: {
        'filter': 'blur(0px)',
        '-webkit-filter': 'blur(0px)'
      }
    }
  },
  computed: {
    maxBlur () {
      if (this.$vuetify.breakpoint.smAndDown) {
        return 20
      }

      return 40
    }
  },
  created () {
    window.addEventListener('scroll', this.onRequestScroll)
    window.addEventListener('resize', this.onResize)
  },
  mounted () {
    this.onResize()
  },
  destroyed () {
    document.removeEventListener('mousemove', this.onRequestMouseMove)
    window.removeEventListener('scroll', this.onRequestScroll)
    window.removeEventListener('resize', this.onResize)
  },
  watch: {
    gifFallback (newValue) {
      if (newValue) {
        this.$set(this.sectionStyle, 'background-image', 'url(/assets/img/organism.gif)')
        document.removeEventListener('mousemove', this.onRequestMouseMove)
      } else {
        this.$set(this.sectionStyle, 'background-image', null)
        document.addEventListener('mousemove', this.onRequestMouseMove)
      }
    }
  },
  methods: {
    onResize () {
      const height = window.innerHeight ||
         document.documentElement.clientHeight ||
         document.body.clientHeight
      this.maxWindow = height

      this.testSupportedAnimation()
    },
    testSupportedAnimation () {
      if (!this.$store.getters.supportsVariableFonts) {
        // eslint-disable-next-line
        console.warn('No variable font support')
        this.gifFallback = true
        return
      }

      if (this.$vuetify.breakpoint.smAndDown) {
        this.setupGyro()
      } else {
        this.teardownGyro()
      }
    },
    setupGyro () {
      document.removeEventListener('mousemove', this.onRequestMouseMove)
      this.gn.init()
        .then(() => {
          this.gn.start(this.requestGyro)
        })
        .catch(error => {
          // eslint-disable-next-line
          console.error(error)
          this.gifFallback = true
        })
    },
    requestGyro (data) {
      if (this.gyroTicking) {
        return
      }

      this.gyroTicking = true
      window.requestAnimationFrame(() => {
        this.onGyro(data)
      })
    },
    onGyro (data) {
      // NB: draw parabolas in for both axis and take the average
      const wghtX = Math.pow(data.dm.gx, 2) / 0.25 + (this.maxWght / 2)
      const wghtY = Math.pow(data.dm.gy, 2) / 0.4 + (this.maxWght / 2)
      const wght = Math.min(Math.round((wghtY + wghtX) / 2), this.maxWght)
      this.$set(this.titleStyle, 'font-variation-settings', `"wght" ${wght}`)
      this.gyroTicking = false
    },
    teardownGyro () {
      this.gn.end()
      document.addEventListener('mousemove', this.onRequestMouseMove)
    },
    mapRange (value, minIn, maxIn, minOut, maxOut) {
      return (value - minIn) * (maxOut - minOut) / (maxIn - minIn) + minOut
    },
    onRequestMouseMove (event) {
      if (this.tickingMouse) {
        return
      }

      this.tickingMouse = true
      window.requestAnimationFrame(() => {
        this.onMouseMove(event)
      })
    },
    onMouseMove (event) {
      const x1 = this.minWindow
      const x2 = this.maxWindow / 2
      const x3 = this.maxWindow
      const y1 = this.maxWght
      const y2 = this.minWght
      const y3 = this.maxWght
      const value = event.clientY

      // https://math.stackexchange.com/questions/889569/finding-a-parabola-from-three-points-algebraically
      // https://en.wikipedia.org/wiki/Lagrange_polynomial
      const wght = (y1 * (value - x2) * (value - x3)) / ((x1 - x2) * (x1 - x3)) +
        (y2 * (value - x1) * (value - x3)) / ((x2 - x1) * (x2 - x3)) +
        (y3 * (value - x1) * (value - x2)) / ((x3 - x1) * (x3 - x2))

      this.$set(this.titleStyle, 'font-variation-settings', `"wght" ${Math.round(wght)}`)
      this.tickingMouse = false
    },
    onRequestScroll (event) {
      if (this.tickingScroll) {
        return
      }

      this.tickingScroll = true
      window.requestAnimationFrame(() => {
        this.onScroll(event)
      })
    },
    onScroll (event) {
      const top = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0)
      let blur = this.mapRange(top, this.minWindow, this.maxWindow, this.minBlur, this.maxBlur)
      if (blur > this.maxBlur) {
        blur = this.maxBlur
      }

      this.$set(this.sectionStyle, 'filter', `blur(${blur}px)`)
      this.$set(this.sectionStyle, '-webkit-filter', `blur(${blur}px)`)
      this.tickingScroll = false
    }
  }
}
</script>
<style lang="stylus">
@require '~vuetify/src/stylus/settings/_variables.styl'
@require '../assets/styles/base'

.title-animation-section
  position: fixed
  top: 0
  left: 0
  height: 100%
  width: 100%
  transform translate3d(0, 0, 0)
  background-size cover
  background-repeat no-repeat
  background-position center
  perspective 1000
  backface-visibility hidden
  user-select none
  @media $display-breakpoints.sm-and-down
    background-size 150%
  @media print
    display none
  .layout
    height 100%
  .title-animation
    width: 100%
    font-size: 32.5rem
    font-family: $font-family-variable
    position: relative
    top: -4rem
    color: #9fadf5
    text-shadow rgba(159, 173, 245, 0.75) 1px 1px 0.5rem,
      rgba(159, 173, 245, 0.75) -1px -1px 0.5rem,
      rgba(159, 173, 245, 0.75) -1px 1px 0.5rem,
      rgba(159, 173, 245, 0.75) 1px -1px 0.5rem
    @media $display-breakpoints.sm-and-down
      top -2.5rem
      font-size 20rem
</style>
